/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { getCookie } from 'cookies-next';
import { isJwtExpired } from './decode-jwt';

/**
 * Single source of truth cho TOKEN của session WEBINAPP (login bằng token trên URL
 * từ app Ahamove).
 *
 * Vì sao cần module riêng (KHÔNG đọc thẳng cookie `token`):
 *  - Trên thiết bị thật thường tồn tại NHIỀU cookie `token` ở scope khác nhau
 *    (host-only vs `domain=.ahafood.ai`, hoặc sót lại từ phiên web account khác).
 *    `getCookie('token')` trả về cookie nào là KHÔNG xác định → có máy gọi API/sync
 *    bằng token CŨ của account khác (bug prod "chỉ vài máy").
 *  - Token URL phải là token CHÍNH, dùng xuyên suốt: hydrate, Bearer header, body
 *    request, và call /api/auth/sync (chỉ để kích hoạt phiên). KHÔNG bao giờ dùng
 *    token trả về từ login API.
 *
 * Thứ tự tin cậy khi đọc: module var (token URL của phiên hiện tại) → localStorage
 * bền (sống qua reload/điều hướng) → cookie (chốt chặn cuối, có thể nhập nhằng).
 *
 * Leaf module: chỉ import cookies-next + decode-jwt → KHÔNG tạo circular dep với
 * provider/interceptor (get-token.ts cũng import từ đây).
 */

const COOKIE_TOKEN = 'token';
const DURABLE_KEY = 'ahafood.webinapp_token';
const MAX_AGE_SESSION = 30 * 24 * 60 * 60; // 30 ngày — match setAuthCookies/setTokenCookieFromUrl

interface DurableToken {
  token: string;
  savedAt: number;
}

/**
 * Token URL của phiên hiện tại. Mất khi full reload (module re-init) — vì vậy còn
 * persist xuống localStorage để đọc lại sau reload/điều hướng.
 */
let webinappTokenMem: string | null = null;

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/** Detect deployed ahafood domain (để set/clear cookie với Domain=.ahafood.ai). */
function isAhafoodDomain(): boolean {
  if (!isBrowser()) return false;
  return window.location.hostname.endsWith('.ahafood.ai');
}

/**
 * Xóa MỌI biến thể cookie `token` đã biết: host-only (path=/) + domain=.ahafood.ai.
 * Best-effort hygiene — KHÔNG xóa được biến thể có `path` lạ (không đọc được path
 * từ JS); tính đúng đắn đảm bảo bằng thứ tự đọc trong readWebinappToken, không phải
 * bằng việc xóa.
 */
export function expireTokenCookieVariants() {
  if (!isBrowser()) return;
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${COOKIE_TOKEN}=; path=/; ${expire}`;
  if (isAhafoodDomain()) {
    document.cookie = `${COOKIE_TOKEN}=; path=/; domain=.ahafood.ai; ${expire}`;
  }
}

/**
 * Đặt token URL làm token CHÍNH của phiên webinapp:
 *  1. Xóa mọi biến thể cookie `token` cũ (tránh nhập nhằng/cross-account).
 *  2. Set DUY NHẤT 1 cookie `token` canonical (domain trên ahafood, Secure trên
 *     https, SameSite=Lax, 30 ngày) — parity với setTokenCookieFromUrl trước đây.
 *  3. Lưu vào module var + localStorage bền (đọc lại sau reload).
 */
export function persistWebinappToken(token: string) {
  if (!isBrowser() || !token) return;

  expireTokenCookieVariants();

  const isHttps = window.location.protocol === 'https:';
  const parts = [
    `${COOKIE_TOKEN}=${token}`,
    'path=/',
    'SameSite=Lax',
    `max-age=${MAX_AGE_SESSION}`,
  ];
  if (isAhafoodDomain()) parts.push('domain=.ahafood.ai');
  if (isHttps) parts.push('Secure');
  document.cookie = parts.join('; ');

  webinappTokenMem = token;
  try {
    const durable: DurableToken = { token, savedAt: Date.now() };
    localStorage.setItem(DURABLE_KEY, JSON.stringify(durable));
  } catch {
    /* ignore — cookie vẫn là fallback */
  }
}

/** Đọc token bền từ localStorage; bỏ qua nếu JWT đã hết hạn. */
function readDurableToken(): string {
  try {
    const raw = localStorage.getItem(DURABLE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as Partial<DurableToken>;
    const token = parsed?.token;
    if (typeof token !== 'string' || !token) return '';
    if (isJwtExpired(token)) return '';
    return token;
  } catch {
    return '';
  }
}

/**
 * Token webinapp hiện tại theo thứ tự tin cậy:
 *   module var (token URL phiên này) → localStorage bền → cookie `token`.
 * Trả '' nếu không có.
 */
export function readWebinappToken(): string {
  if (!isBrowser()) return '';
  if (webinappTokenMem) return webinappTokenMem;

  const durable = readDurableToken();
  if (durable) {
    // Re-hydrate module var để các lần đọc sau (và resync) ổn định trong phiên.
    webinappTokenMem = durable;
    return durable;
  }

  const cookie = getCookie(COOKIE_TOKEN);
  return typeof cookie === 'string' && cookie ? cookie : '';
}

/** Xóa toàn bộ dấu vết token webinapp (logout): module var + localStorage + cookie. */
export function clearWebinappToken() {
  webinappTokenMem = null;
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(DURABLE_KEY);
  } catch {
    /* ignore */
  }
  expireTokenCookieVariants();
}
